import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { composeCover, composeBackCover } from "@/lib/coverCompose";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Upload, Loader2, ImageIcon, Check, Type, BookOpen } from "lucide-react";
import { toast } from "sonner";

const STYLES = ["editorial", "gothic", "fantasy", "romance", "thriller", "scifi"];

export default function CoverModal({ open, onOpenChange, project, onUpdated }) {
  const { t } = useI18n();

  // Front cover state
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("editorial");
  const [generating, setGenerating] = useState(false);
  const [baseImage, setBaseImage] = useState(null);
  const [composed, setComposed] = useState(null);
  const [applying, setApplying] = useState(false);
  const [overlay, setOverlay] = useState(true);
  const [titleText, setTitleText] = useState("");
  const [authorText, setAuthorText] = useState("");
  const [position, setPosition] = useState("bottom");

  // Back cover state
  const [synopsis, setSynopsis] = useState("");
  const [backPreview, setBackPreview] = useState(null);
  const [applyingBack, setApplyingBack] = useState(false);

  useEffect(() => {
    if (open) {
      setTitleText(project?.title || "");
      setAuthorText(project?.author_name || "");
      setBaseImage(project?.cover_image || null);
      setSynopsis(project?.description || "");
    }
  }, [open, project]);

  // Recompute front preview
  useEffect(() => {
    if (!baseImage) { setComposed(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const out = await composeCover(baseImage, { title: titleText, author: authorText, position, overlay });
        if (!cancelled) setComposed(out);
      } catch { if (!cancelled) setComposed(baseImage); }
    })();
    return () => { cancelled = true; };
  }, [baseImage, titleText, authorText, position, overlay]);

  // Recompute back preview (debounced while typing)
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const out = await composeBackCover(baseImage, {
          title: titleText, synopsis, author: authorText, imprint: t("cover.imprint"),
        });
        if (!cancelled) setBackPreview(out);
      } catch { if (!cancelled) setBackPreview(null); }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [baseImage, titleText, authorText, synopsis, t]);

  const generate = async () => {
    if (!prompt.trim()) { toast.error(t("cover.prompt")); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/cover`, {
        prompt, style, title: project.title, author_name: project.author_name,
      });
      setBaseImage(data.image);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBaseImage(reader.result);
    reader.readAsDataURL(file);
  };

  const applyFront = async () => {
    const finalImage = overlay ? (composed || baseImage) : baseImage;
    if (!finalImage) return;
    setApplying(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, { cover_image: finalImage });
      onUpdated(data);
      toast.success(t("toast.coverApplied"));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setApplying(false); }
  };

  const applyBack = async () => {
    if (!backPreview) return;
    setApplyingBack(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, { back_cover_image: backPreview });
      onUpdated(data);
      toast.success(t("toast.coverApplied"));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setApplyingBack(false); }
  };

  const frontSrc = overlay ? composed : baseImage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><ImageIcon className="w-5 h-5 text-gold" /> {t("cover.title")}</DialogTitle></DialogHeader>

        <Tabs defaultValue="front" className="mt-2">
          <TabsList className="bg-ink-900 border border-white/10">
            <TabsTrigger value="front" data-testid="cover-section-front" className="data-[state=active]:bg-gold data-[state=active]:text-ink-900 text-xs"><ImageIcon className="w-3.5 h-3.5 mr-1.5" />{t("cover.tab.front")}</TabsTrigger>
            <TabsTrigger value="back" data-testid="cover-section-back" className="data-[state=active]:bg-gold data-[state=active]:text-ink-900 text-xs"><BookOpen className="w-3.5 h-3.5 mr-1.5" />{t("cover.tab.back")}</TabsTrigger>
          </TabsList>

          {/* ============ FRONT ============ */}
          <TabsContent value="front" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-5">
                <Tabs defaultValue="ai">
                  <TabsList className="bg-ink-900 border border-white/10">
                    <TabsTrigger value="ai" data-testid="cover-tab-ai" className="data-[state=active]:bg-gold data-[state=active]:text-ink-900 text-xs"><Sparkles className="w-3.5 h-3.5 mr-1.5" />{t("cover.tab.ai")}</TabsTrigger>
                    <TabsTrigger value="upload" data-testid="cover-tab-upload" className="data-[state=active]:bg-gold data-[state=active]:text-ink-900 text-xs"><Upload className="w-3.5 h-3.5 mr-1.5" />{t("cover.tab.upload")}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="ai" className="space-y-4 mt-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-slate-400">{t("cover.prompt")}</Label>
                      <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t("cover.promptPlaceholder")} rows={3} className="bg-ink-900 border-white/10 focus-visible:ring-gold resize-none" data-testid="cover-generator-prompt-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-slate-400">{t("cover.style")}</Label>
                      <Select value={style} onValueChange={setStyle}>
                        <SelectTrigger className="bg-ink-900 border-white/10" data-testid="cover-style-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                          {STYLES.map((s) => <SelectItem key={s} value={s}>{t(`cover.style.${s}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={generate} disabled={generating} className="w-full bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="cover-generator-generate-btn">
                      {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("cover.generating")}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t("cover.generate")}</>}
                    </Button>
                  </TabsContent>
                  <TabsContent value="upload" className="mt-4">
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-white/15 rounded-xl py-10 cursor-pointer hover:border-gold/40 transition-colors" data-testid="cover-upload-label">
                      <Upload className="w-8 h-8 text-slate-500 mb-3" />
                      <span className="text-sm text-slate-400">{t("cover.upload")}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="cover-upload-input" />
                    </label>
                  </TabsContent>
                </Tabs>

                <div className="rounded-xl border border-white/10 bg-ink-900/50 p-4 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 text-sm text-slate-200"><Type className="w-4 h-4 text-gold" /> {t("cover.overlay")}</Label>
                    <Switch checked={overlay} onCheckedChange={setOverlay} data-testid="cover-overlay-switch" />
                  </div>
                  {overlay && (
                    <div className="space-y-3 fade-up">
                      <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wide text-slate-500">{t("cover.titleText")}</Label>
                        <Input value={titleText} onChange={(e) => setTitleText(e.target.value)} className="bg-ink-900 border-white/10 focus-visible:ring-gold h-9" data-testid="cover-title-text" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wide text-slate-500">{t("cover.authorText")}</Label>
                        <Input value={authorText} onChange={(e) => setAuthorText(e.target.value)} className="bg-ink-900 border-white/10 focus-visible:ring-gold h-9" data-testid="cover-author-text" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wide text-slate-500">{t("cover.position")}</Label>
                        <Select value={position} onValueChange={setPosition}>
                          <SelectTrigger className="bg-ink-900 border-white/10 h-9" data-testid="cover-position-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                            <SelectItem value="bottom">{t("cover.pos.bottom")}</SelectItem>
                            <SelectItem value="top">{t("cover.pos.top")}</SelectItem>
                            <SelectItem value="center">{t("cover.pos.center")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex-1 aspect-[2/3] rounded-xl overflow-hidden bg-ink-900 border border-white/10 flex items-center justify-center" data-testid="cover-preview">
                  {generating ? (
                    <div className="w-full h-full shimmer flex items-center justify-center"><Sparkles className="w-8 h-8 text-gold/40 pulse-gold" /></div>
                  ) : frontSrc ? (
                    <img src={frontSrc} alt="cover preview" className="w-full h-full object-cover" data-testid="cover-preview-img" />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-slate-700" />
                  )}
                </div>
                <Button onClick={applyFront} disabled={!baseImage || applying} className="mt-4 bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="cover-apply-btn">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> {t("cover.apply")}</>}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ============ BACK ============ */}
          <TabsContent value="back" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <p className="text-xs text-slate-500">{t("cover.backHint")}</p>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-400">{t("cover.titleText")}</Label>
                  <Input value={titleText} onChange={(e) => setTitleText(e.target.value)} className="bg-ink-900 border-white/10 focus-visible:ring-gold h-9" data-testid="back-title-text" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-400">{t("cover.synopsis")}</Label>
                  <Textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} placeholder={t("cover.synopsisPlaceholder")} rows={7} className="bg-ink-900 border-white/10 focus-visible:ring-gold resize-none" data-testid="back-synopsis-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-400">{t("cover.authorText")}</Label>
                  <Input value={authorText} onChange={(e) => setAuthorText(e.target.value)} className="bg-ink-900 border-white/10 focus-visible:ring-gold h-9" data-testid="back-author-text" />
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex-1 aspect-[2/3] rounded-xl overflow-hidden bg-ink-900 border border-white/10 flex items-center justify-center" data-testid="back-cover-preview">
                  {backPreview ? (
                    <img src={backPreview} alt="back cover preview" className="w-full h-full object-cover" data-testid="back-cover-preview-img" />
                  ) : (
                    <BookOpen className="w-10 h-10 text-slate-700" />
                  )}
                </div>
                <Button onClick={applyBack} disabled={!backPreview || applyingBack} className="mt-4 bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="back-cover-apply-btn">
                  {applyingBack ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> {t("cover.applyBack")}</>}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
